import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { API_URL } from '@/src/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/src/shadComponents/ui/card';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';

const upgradeMessage = 'The payment service needs an update before these settings can be changed. Saved values are shown below. Reload settings after the update.';
const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
function savedConfiguration(value: any, activationId?: number) {
    if (!activationId) return value;
    // A legacy response stores the activation keys at the top level. Never copy
    // an inherited organization/system account into an activation override.
    if (Object.prototype.hasOwnProperty.call(value, 'override')) return value.override;
    return value.stripeMode !== 'inherit' ? value : null;
}
function supportsSettings(value: any, activationId?: number) {
    return !!value && typeof value === 'object' && (!activationId ||
        (Object.prototype.hasOwnProperty.call(value, 'override') && typeof value.stripeSource === 'string'));
}

export default function PaymentConfiguration({ activationId, onSaved }: { activationId?: number; onSaved?: (settings: any) => void }) {
    const api = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const inputId = useId();
    const path = activationId ? `/activations/${activationId}/paymentSettings` : `/${orgId}/org/settings/payments`;
    const [settings, setSettings] = useState<any>(null);
    const [draft, setDraft] = useState({ cost: '5.00', taxEnabled: false, taxRate: '0', mode: 'inherit', environment: 'live', publishable: '', secret: '', webhook: '' });
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [reload, setReload] = useState(0);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const request = useRef(0);
    const apply = useCallback((value: any) => {
        if (!value || typeof value !== 'object') throw new Error('Missing payment settings');
        const configuration = savedConfiguration(value, activationId);
        setSettings(value);
        setDraft({ cost: ((value.costInCents ?? 0) / 100).toFixed(2), taxEnabled: value.taxEnabled ?? false,
            taxRate: String(value.taxRatePercent ?? ((value.taxRatePpm ?? 0) / 10000)),
            mode: value.stripeMode ?? (value.hasStripeKeys || value.stripePublishableKey ? 'override' : 'inherit'),
            environment: configuration?.stripeEnvironment || 'live',
            publishable: configuration?.stripePublishableKey || '', secret: '', webhook: '' });
    }, [activationId]);
    useEffect(() => {
        const current = ++request.current;
        setLoaded(false); setBusy(false); setMessage(''); setError('');
        api.get(path).then(r => {
            if (current === request.current) { apply(r.data); setLoaded(true); }
        }).catch(() => {
            if (current === request.current) setError('Payment settings could not be loaded. Check your admin access and try again.');
        });
        return () => { request.current = current + 1; };
    }, [api, path, apply, reload]);
    const compatible = supportsSettings(settings, activationId);
    const configuration = settings && savedConfiguration(settings, activationId);
    const update = (field: string, value: any) => setDraft(d => ({ ...d, [field]: value }));
    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy || !loaded || !compatible) return;
        setError(''); setMessage('');
        const cents = Math.round(Number(draft.cost) * 100);
        if (activationId && (!/^\d+(\.\d{1,2})?$/.test(draft.cost) || !Number.isSafeInteger(cents))) {
            setError('Enter a price with up to two decimal places.'); return;
        }
        if (activationId && (!/^\d{1,3}(\.\d{1,4})?$/.test(draft.taxRate) || Number(draft.taxRate) > 100)) {
            setError('Enter a tax rate from 0 to 100, with up to four decimal places.'); return;
        }
        if (!activationId || draft.mode === 'override') {
            const publishable = draft.publishable.trim(), secret = draft.secret.trim();
            if (publishable && !new RegExp(`^pk_${draft.environment}_`).test(publishable)) {
                setError(`Publishable key must start with pk_${draft.environment}_.`); return;
            }
            if (secret && !new RegExp(`^(sk|rk)_${draft.environment}_`).test(secret)) {
                setError(`Secret or restricted key must start with sk_${draft.environment}_ or rk_${draft.environment}_.`); return;
            }
            if (configuration?.stripePublishableKey && !publishable) {
                setError('Keep the saved publishable key or enter its replacement. To use organization defaults, choose Inherit organization defaults.'); return;
            }
        }
        const current = request.current;
        setBusy(true);
        try {
            const body: any = activationId ? { activationId, costInCents: cents, taxEnabled: draft.taxEnabled, taxRatePercent: draft.taxRate, stripeMode: draft.mode } : {};
            if (!activationId || draft.mode === 'override') {
                const changedMode = activationId && draft.mode !== settings.stripeMode;
                if (draft.publishable.trim() && (changedMode || draft.publishable.trim() !== configuration?.stripePublishableKey)) body.stripePublishableKey = draft.publishable.trim();
                if (changedMode || draft.environment !== configuration?.stripeEnvironment) body.stripeEnvironment = draft.environment;
                if (draft.secret.trim()) body.stripeSecretKey = draft.secret.trim();
                if (draft.webhook.trim()) body.stripeWebhookSecret = draft.webhook.trim();
            }
            const result = await api.put(path, body);
            if (current !== request.current) return;
            if (!supportsSettings(result.data, activationId)) {
                setLoaded(false);
                setError('Could not confirm the saved settings. The payment service returned an older response. Reload settings after updating the service.');
                return;
            }
            const returnedConfiguration = savedConfiguration(result.data, activationId);
            const matches = (!activationId || (result.data.costInCents === cents &&
                (result.data.taxEnabled ?? false) === draft.taxEnabled &&
                Number(result.data.taxRatePercent ?? 0) === Number(draft.taxRate) && result.data.stripeMode === draft.mode)) &&
                (!body.stripePublishableKey || returnedConfiguration?.stripePublishableKey === body.stripePublishableKey) &&
                (!body.stripeEnvironment || returnedConfiguration?.stripeEnvironment === body.stripeEnvironment);
            if (!matches) {
                setLoaded(false);
                setError('Could not confirm the saved settings. The server returned different values. Reload settings before trying again.');
                return;
            }
            apply(result.data); onSaved?.(result.data);
            setMessage('Payment settings saved. Existing purchases keep their original settings.');
        } catch (e: any) {
            if (current === request.current) setError(e.response?.data?.message || 'Could not save payment settings.');
        } finally { if (current === request.current) setBusy(false); }
    };
    const field = (label: string, key: 'publishable' | 'secret' | 'webhook', placeholder: string) => <div className="space-y-2">
        <Label htmlFor={`${inputId}-${key}`}>{label}</Label>
        <Input id={`${inputId}-${key}`} type={key === 'publishable' ? 'text' : 'password'}
            autoComplete="new-password" spellCheck={false} value={draft[key]} placeholder={placeholder}
            onChange={e => update(key, e.target.value)} />
    </div>;
    const source = settings?.stripeSource === 'activation' ? 'Activation override'
        : settings?.stripeSource === 'organization' ? 'Organization defaults'
        : settings?.stripeSource === 'system' ? 'System defaults' : 'Saved activation settings';
    return <Card className="w-full min-w-0">
        <CardHeader>
            <CardTitle>{activationId ? 'Payment settings' : 'Organization Stripe defaults'}</CardTitle>
            <CardDescription>{activationId
                ? 'Set the default session price, sales tax, and Stripe account for this activation.'
                : 'Activations inherit this Stripe account unless they use an activation override. Sales tax is configured per activation.'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
            {!loaded && !error && <p className="text-sm text-muted-foreground">Loading payment settings…</p>}
            {loaded && !compatible && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{upgradeMessage}</div>}
            {loaded && <form onSubmit={save} aria-label="Payment settings">
                <fieldset disabled={busy || !compatible} className="min-w-0 space-y-6">
                    {activationId && <div className="grid gap-6 lg:grid-cols-2">
                        <section className="space-y-4" aria-label="Pricing">
                            <h3 className="text-sm font-semibold">Pricing</h3>
                            <div className="max-w-xs space-y-2">
                                <Label htmlFor={`${inputId}-cost`}>Default base price (USD)</Label>
                                <Input id={`${inputId}-cost`} inputMode="decimal" value={draft.cost} onChange={e => update('cost', e.target.value)} />
                            </div>
                            <p className="text-sm text-muted-foreground">Experiences with custom package prices keep those prices. This amount is the default.</p>
                        </section>
                        <section className="space-y-4" aria-label="Sales tax">
                            <h3 className="text-sm font-semibold">Sales tax</h3>
                            <div className="flex items-center gap-2">
                                <input id={`${inputId}-tax-enabled`} type="checkbox" className="h-4 w-4 rounded border-input accent-black" checked={draft.taxEnabled} onChange={e => update('taxEnabled', e.target.checked)} />
                                <Label htmlFor={`${inputId}-tax-enabled`}>Add sales tax</Label>
                            </div>
                            <div className="max-w-xs space-y-2">
                                <Label htmlFor={`${inputId}-tax-rate`}>Sales tax rate (%)</Label>
                                <Input id={`${inputId}-tax-rate`} inputMode="decimal" disabled={!draft.taxEnabled} value={draft.taxRate} onChange={e => update('taxRate', e.target.value)} />
                            </div>
                            <p className="text-sm text-muted-foreground">Added to the base price and itemized at checkout. Show “+ tax” alongside prices in the experience.</p>
                        </section>
                    </div>}
                    <section className={`${activationId ? 'border-t pt-6 ' : ''}space-y-4`} aria-label="Stripe account">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="text-sm font-semibold">Stripe account</h3>
                            {compatible && <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">{source} · {settings?.hasStripeKeys ? 'Keys saved' : 'Setup incomplete'}</span>}
                        </div>
                        {activationId && <div className="max-w-md space-y-2">
                            <Label htmlFor={`${inputId}-mode`}>Stripe configuration</Label>
                            <select id={`${inputId}-mode`} className={selectClass} value={draft.mode} onChange={e => update('mode', e.target.value)}>
                                <option value="inherit">Inherit organization defaults</option><option value="override">Override for this activation</option>
                            </select>
                            <p className="text-sm text-muted-foreground">An override uses a separate Stripe account for this activation.</p>
                        </div>}
                        {(!activationId || draft.mode === 'override') && <>
                            <div className="max-w-xs space-y-2">
                                <Label htmlFor={`${inputId}-environment`}>Environment</Label>
                                <select id={`${inputId}-environment`} className={selectClass} value={draft.environment} onChange={e => update('environment', e.target.value)}>
                                    <option value="test">Test</option><option value="live">Live</option>
                                </select>
                            </div>
                            <div className="grid gap-4 lg:grid-cols-2">
                                {field('Publishable key', 'publishable', `pk_${draft.environment}_…`)}
                                {field('Secret or restricted key', 'secret', configuration?.hasStripeKeys ? 'Leave blank to keep the saved key' : `rk_${draft.environment}_…`)}
                            </div>
                            <p className="text-sm text-muted-foreground">Secret keys stay hidden. Leave blank to keep a saved key. Restricted keys need Payment Intents write access.</p>
                            <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                                {field('Webhook signing secret', 'webhook', configuration?.hasStripeWebhookSecret ? 'Leave blank to keep the saved secret' : 'whsec_…')}
                                <div className="space-y-2 text-sm">
                                    <p className="font-medium">Stripe webhook URL</p>
                                    <code className="block break-all rounded border bg-background p-3 text-xs">{API_URL}/payments/stripe/webhook/{activationId ? `activation/${activationId}` : `organization/${orgId}`}</code>
                                    <p className="text-muted-foreground">Configure Stripe to send payment_intent and charge refund events to this URL.</p>
                                </div>
                            </div>
                        </>}
                    </section>
                    <div className="flex flex-wrap items-center gap-4 border-t pt-5">
                        <Button type="submit" disabled={busy || !compatible}>{busy ? 'Saving…' : 'Save payment settings'}</Button>
                        <p className="text-sm text-muted-foreground">Changes apply to new purchases.</p>
                    </div>
                </fieldset>
            </form>}
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            {message && <p role="status" className="text-sm text-green-700">{message}</p>}
            {(!loaded && error) || (loaded && !compatible) ? <Button type="button" variant="outline" onClick={() => setReload(n => n + 1)}>Reload settings</Button> : null}
        </CardContent>
    </Card>;
}
